import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, SessionExpiredError } from "@web/lib/api";
import { PAGE_TITLE_ID } from "@web/app/pageTitle";
import { fetchSignInTexts, saveSignInTexts } from "../platform/signInTextsApi";
import {
  EMPTY_VALUES,
  SIGN_IN_TEXT_FIELDS,
  SIGN_IN_TEXT_MAX_LENGTH,
  hasOverrides,
  isDirty,
  refusedFields,
  signInTextsErrorKey,
  toFormValues,
  toSaveRequest,
  type SignInTextField,
  type SignInTexts,
  type SignInTextValues,
} from "../platform/signInTexts";

// KIRJAUTUMISSIVUN TEKSTIT — the platform operator's own words in the brand panel of every sign-in page
// (owner's decision B1, 4.11.2026, task dfbe33eb). Four fields: the iskulause and the line under it, each
// in Finnish and in English.
//
// WHY A PAGE OF ITS OWN AND NOT A SECTION ON `/parameters`, which the task left to be chosen and argued.
// Three reasons, and the first is the one that decides it:
//
//   1. `/parameters` is the KYSELYMOOTTORI's platform-class parameters (KM-E9 / US-E9.2b, spec luku 7.3),
//      and its whole premise is that nothing on it may be typed: the factor library ships with the
//      software and a platform-wide k raise is a deployment decision, not a request a form should be able
//      to make. Its own header says so. An editable brand form in that frame would contradict the page's
//      one claim, and an operator who learnt "this page states values nobody sets" would have to unlearn
//      it at the first section that does.
//   2. The two have no audience in common beyond "a platform operator": one is read when a questionnaire's
//      sizing is in question, the other when marketing changes a slogan. Nothing is compared across them.
//   3. The console's navigation is its table of contents, and this is the ONE platform-wide thing an
//      operator can edit. A row named for it is how it is found at all; a section three screens down a
//      page about questionnaire parameters is not.
//
// WHAT AN EMPTY FIELD MEANS, which is the whole of the form's semantics: the translation file's shipped
// default is in force for that text, in that language. The field shows that default as its PLACEHOLDER —
// read in the field's OWN language rather than the console's, so the English input shows the English
// default even to an operator working in Finnish — so "what happens if I leave this blank" is answered by
// looking at the box. "Palauta oletukset" empties all four and saves, which is the same PUT with four
// nulls; there is no second route for it and no per-field reset, because a field is restored by clearing
// it, which is one keystroke fewer than a button beside it would be.
//
// The form is REPLACE-shaped end to end, like the route: what is submitted is what will be in force, and
// the answer to the write is adopted as the new state rather than guessed at.

/** The read, which is what the form is built on. */
type Load =
  | { phase: "loading" }
  | { phase: "error" }
  | { phase: "ready"; texts: SignInTexts };

/** What a save ended as: a banner above the form. `saved` clears the moment anything is typed. */
type Outcome = { kind: "saved" } | { kind: "failed"; key: string; field: SignInTextField | null };

export function PlatformSignInTextsPage() {
  const { t } = useTranslation();
  const [load, setLoad] = useState<Load>({ phase: "loading" });
  const [values, setValues] = useState<SignInTextValues>(EMPTY_VALUES);
  const [saving, setSaving] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  /** Per-field refusals, raised on submit rather than per keystroke — a half-typed text is not a mistake. */
  const [refusals, setRefusals] = useState<Partial<Record<SignInTextField, string>>>({});

  useEffect(() => {
    const controller = new AbortController();
    fetchSignInTexts(controller.signal)
      .then((texts) => {
        if (controller.signal.aborted) return;
        setLoad({ phase: "ready", texts });
        setValues(toFormValues(texts));
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || err instanceof SessionExpiredError) return;
        setLoad({ phase: "error" });
      });
    return () => controller.abort();
  }, []);

  const edit = useCallback((field: SignInTextField, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    // A receipt describes the state as it was saved; the moment the operator types, it no longer does.
    setOutcome(null);
    setRefusals((current) => {
      if (!(field in current)) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }, []);

  /**
   * Submit `next`. Both buttons go through here — saving what is typed, and restoring the defaults, which
   * is the same write with four empty fields. One path, so the two endings cannot drift apart.
   */
  const submit = useCallback(
    (next: SignInTextValues) => {
      if (saving) return;

      const refused = refusedFields(next);
      if (refused.length > 0) {
        setRefusals(Object.fromEntries(refused.map(({ field, key }) => [field, key])));
        setOutcome(null);
        return;
      }

      setRefusals({});
      setOutcome(null);
      setSaving(true);
      saveSignInTexts(toSaveRequest(next))
        .then((saved) => {
          // The server's answer, not the form's own guess: what is in force is what it reported.
          setLoad({ phase: "ready", texts: saved });
          setValues(toFormValues(saved));
          setOutcome({ kind: "saved" });
        })
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError) return;
          const status = err instanceof ApiError ? err.status : null;
          const detail = err instanceof ApiError ? (err.problem?.detail ?? null) : null;
          const { key, field } = signInTextsErrorKey(status, detail);
          setOutcome({ kind: "failed", key, field });
        })
        .finally(() => setSaving(false));
    },
    [saving],
  );

  const texts = load.phase === "ready" ? load.texts : null;
  const dirty = texts !== null && isDirty(values, texts);
  const canRestore =
    texts !== null && (hasOverrides(values) || Object.values(texts).some((v) => v !== null));
  /**
   * The field the SERVER refused, when it named one (a 422's detail begins with the parameter name).
   *
   * It marks the input `aria-invalid` and nothing more: the sentence stays in the banner, where the other
   * endings' sentences are, because a 422 means the console and the platform disagreed about this text —
   * and the console has already said its own piece about it, in `refusals`, or it would not have
   * submitted. Two messages under one input would be two explanations of one refusal.
   */
  const serverRefusedField = outcome?.kind === "failed" ? outcome.field : null;

  return (
    <section className="sign-in-texts" aria-labelledby={PAGE_TITLE_ID} data-testid="sign-in-texts-page">
      <p className="sign-in-texts__lead">{t("platformSignInTexts.subtitle")}</p>
      {/* THE SCOPE, said once and up front: these words go out to every customer. It is the one fact an
          operator has to have read before typing, and the page is small enough that a banner would be
          louder than the act deserves. */}
      <p className="sign-in-texts__intro">{t("platformSignInTexts.hint")}</p>

      {load.phase === "loading" && (
        <p className="empty-state" role="status" aria-live="polite" data-testid="sign-in-texts-loading">
          {t("platformSignInTexts.loading")}
        </p>
      )}

      {load.phase === "error" && (
        <p className="banner banner--error" role="alert" data-testid="sign-in-texts-load-error">
          {t("platformSignInTexts.loadError")}
        </p>
      )}

      {load.phase === "ready" && (
        <>
          {outcome?.kind === "saved" && (
            <p className="banner banner--success" role="status" data-testid="sign-in-texts-saved">
              {t("platformSignInTexts.saved")}
            </p>
          )}
          {outcome?.kind === "failed" && (
            <p className="banner banner--error" role="alert" data-testid="sign-in-texts-error">
              {t(outcome.key)}
            </p>
          )}

          <form
            className="form sign-in-texts__form"
            data-testid="sign-in-texts-form"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              submit(values);
            }}
          >
            {SIGN_IN_TEXT_FIELDS.map(({ field, language, defaultKey, labelKey }) => {
              const inputId = `sign-in-text-${field}`;
              const hintId = `${inputId}-hint`;
              // The shipped text this field overrides, read in the FIELD's language and not the console's:
              // an operator working in Finnish still has to see what an English visitor would read.
              const shipped = t(defaultKey, { lng: language });
              const refusal = refusals[field];
              return (
                <div className="sign-in-texts__row" key={field}>
                  <label htmlFor={inputId}>{t(labelKey)}</label>
                  <input
                    id={inputId}
                    type="text"
                    value={values[field]}
                    placeholder={shipped}
                    maxLength={SIGN_IN_TEXT_MAX_LENGTH}
                    aria-describedby={hintId}
                    aria-invalid={refusal || serverRefusedField === field ? true : undefined}
                    disabled={saving}
                    onChange={(e) => edit(field, e.target.value)}
                    data-testid={inputId}
                  />
                  {/* The one sentence that makes the blank field legible: empty means the shipped text,
                      and the shipped text is the one in the box behind the cursor. */}
                  <p className="sign-in-texts__hint" id={hintId} data-testid={`${inputId}-hint`}>
                    {t("platformSignInTexts.fieldHint", { shipped })}
                  </p>
                  {refusal && (
                    <p className="field-error" role="alert" data-testid={`${inputId}-error`}>
                      {t(refusal, { max: SIGN_IN_TEXT_MAX_LENGTH })}
                    </p>
                  )}
                </div>
              );
            })}

            <div className="sign-in-texts__actions">
              <button
                type="submit"
                className="btn btn--primary"
                disabled={saving || !dirty}
                data-testid="sign-in-texts-submit"
              >
                {saving ? t("platformSignInTexts.saving") : t("platformSignInTexts.submit")}
              </button>
              {/* Clearing all four IS the restore, so this is the ordinary write with an emptied form —
                  not a route of its own, and not something the operator could not also do by hand. */}
              <button
                type="button"
                className="btn"
                disabled={saving || !canRestore}
                onClick={() => {
                  setValues(EMPTY_VALUES);
                  submit(EMPTY_VALUES);
                }}
                data-testid="sign-in-texts-restore"
              >
                {t("platformSignInTexts.restore")}
              </button>
            </div>
          </form>
        </>
      )}
    </section>
  );
}
